import { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import { Title, Subtitle, Card, Skeleton } from '../../../../../shared';
import {
    getQuest as getQuestSelector,
    getQuestSolution as getQuestSolutionSelector,
    getIsLoading as getIsLoadingSelector
} from '../../../../../redux/quests/selectors';
import { useSelector } from 'react-redux';
import { color } from '../../../../../theme/color/color';
import { Container } from './styled';
import Description from '../../../components/Description';
import { QuestSolutionPullRequest } from '../../../../../types/quests';
import AttachmentPopup from '../../../../../shared/AttachmentPopup';
import useAppDispatch from '../../../../../hooks/useAppDispatch';
import { getViewUrl } from '../../../../../redux/attachments/service';
import AttachmentItem from '../../../../../shared/AttachmentItem';
import { IAttachment } from '../../../../../types/attachments';

interface ReviewSectionProps {}

const ReviewSection: FC<ReviewSectionProps> = () => {
    const dispatch = useAppDispatch();
    const quest = useSelector(getQuestSelector);
    const questSolution = useSelector(getQuestSolutionSelector);
    const isLoading = useSelector(getIsLoadingSelector);

    const [popupState, setPopupState] = useState({
        isOpen: false,
        url: '',
        type: '',
        name: '',
        currentIndex: 0,
        fileId: 0 as number
    });

    const showSkeleton = isLoading || !quest || !questSolution;

    const getAttachmentUrl = async (file: IAttachment, index: number) => {
        try {
            if (!file.fileId) {
                console.error('File ID is missing:', file);
                return;
            }
            const response = await dispatch(
                getViewUrl({ fileId: file.fileId })
            ).unwrap();

            setPopupState({
                isOpen: true,
                url: response.url,
                type: file.type,
                name: file.name,
                currentIndex: index,
                fileId: file.fileId
            });
        } catch (error) {
            console.error('Error fetching attachment URL:', error);
        }
    };

    const closePopup = () => {
        setPopupState((prev) => ({ ...prev, isOpen: false }));
    };

    const handleNextAttachment = async () => {
        if (
            !questSolution?.attachments ||
            questSolution.attachments.length === 0
        )
            return;

        const nextIndex =
            (popupState.currentIndex + 1) % questSolution.attachments.length;
        const nextFile = questSolution.attachments[nextIndex];

        await getAttachmentUrl(nextFile, nextIndex);
    };

    return (
        <Container>
            <AttachmentPopup
                isOpen={popupState.isOpen}
                onClose={closePopup}
                url={popupState.url}
                type={popupState.type}
                name={popupState.name}
                currentIndex={popupState.currentIndex + 1}
                totalItems={questSolution?.attachments?.length || 0}
                onNext={handleNextAttachment}
                fileId={popupState.fileId}
            />
            <Subtitle>Request Title</Subtitle>
            {showSkeleton ? (
                <Skeleton />
            ) : (
                <Title>{quest?.title || 'Request Title Not Found'}</Title>
            )}
            <Card sx={{ marginTop: '20px' }}>
                <Description
                    label="Relevant PRs"
                    description={
                        <div>
                            {showSkeleton ? (
                                <Skeleton width={300} />
                            ) : questSolution?.questSolutionPrs.length > 0 ? (
                                questSolution.questSolutionPrs.map(
                                    (pr: QuestSolutionPullRequest) => (
                                        <Link
                                            key={pr.id}
                                            to={pr.prUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{
                                                color: color.lightningYellow,
                                                display: 'block',
                                                marginBottom: '5px',
                                                wordBreak: 'break-all',
                                                fontSize: '14px'
                                            }}
                                        >
                                            {pr.prUrl}
                                        </Link>
                                    )
                                )
                            ) : (
                                <span style={{ color: color.white90 }}>
                                    No PRs found
                                </span>
                            )}
                        </div>
                    }
                />
                <Description
                    label="Proof of Work"
                    description={
                        showSkeleton ? (
                            <Skeleton width={300} />
                        ) : questSolution?.attachments &&
                          questSolution.attachments.length > 0 ? (
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    flexWrap: 'wrap',
                                    gap: '8px'
                                }}
                            >
                                {questSolution.attachments.map(
                                    (attachedFile: any, index: number) => (
                                        <AttachmentItem
                                            file={attachedFile}
                                            index={index}
                                            getAttachmentUrl={getAttachmentUrl}
                                        />
                                    )
                                )}
                            </div>
                        ) : (
                            <span style={{ color: color.white90 }}>
                                No attachments found
                            </span>
                        )
                    }
                />
            </Card>
        </Container>
    );
};

export default ReviewSection;
